/**
 * Immutable helpers for serialized world/actor JSON. Used by {@link WorldSerializer} static APIs.
 */
import { parseJson } from '../utils/Utils.js';
import { replace_uuids, shortUUID } from '../utils/uuid.js';

import type { SerializedData } from '../utils/serialization/types.js';

export interface AssetPathReference {
  /** JSON path, e.g. `actors[0].prefabName` */
  jsonPath: string;
  value: string;
}

export interface ClassNameReference {
  /** JSON path of the key whose value matched, e.g. `actors[0].$bc` */
  jsonPath: string;
  value: string;
}

/** Keys in serialized data whose string value names a class. */
const CLASS_NAME_KEYS = new Set(['$bc', '$class', 'class']);

function normalizeForCompare(path: string): string {
  return path.startsWith('@project/') ? path : `@project/${path}`;
}

function pathsEqual(a: string, b: string): boolean {
  if (a === b) {
    return true;
  }
  return normalizeForCompare(a) === normalizeForCompare(b);
}

function uniqueStrings(values: string[]): string[] {
  return [...new Set(values.filter(Boolean))];
}

export function collectPrefabPathsFromSerializedData(data: SerializedData): string[] {
  const names: string[] = [];
  const visited = new Set<object>();

  const collect = (value: unknown): void => {
    if (!value || typeof value !== 'object') {
      return;
    }

    if (visited.has(value as object)) {
      return;
    }
    visited.add(value as object);

    if (Array.isArray(value)) {
      for (const item of value) {
        collect(item);
      }
      return;
    }

    const objectValue = value as Record<string, unknown>;
    const base = objectValue.$base;
    if (base && typeof base === 'object' && !Array.isArray(base)) {
      const prefabPath = (base as { $path?: unknown }).$path;
      if (typeof prefabPath === 'string' && prefabPath.length > 0) {
        names.push(prefabPath);
      }
    }

    // Keep this fallback while older files may still carry legacy actor-level prefabName.
    const prefabName = objectValue.prefabName;
    if (typeof prefabName === 'string' && prefabName.length > 0) {
      names.push(prefabName);
    }

    for (const childValue of Object.values(objectValue)) {
      collect(childValue);
    }
  };

  collect(data);
  return uniqueStrings(names);
}

function readRootClassFromRecord(data: Record<string, unknown>): string | undefined {
  if (typeof data.class === 'string' && data.class.length > 0) {
    return data.class;
  }
  const root = data.$root;
  if (!root || typeof root !== 'object' || Array.isArray(root)) {
    return undefined;
  }
  const rootObj = root as Record<string, unknown>;
  if (typeof rootObj.$bc === 'string' && rootObj.$bc.length > 0) {
    return rootObj.$bc;
  }
  if (Object.keys(rootObj).length === 1 && typeof rootObj.$ref === 'number') {
    const objects = data.$objects;
    if (!Array.isArray(objects)) {
      return undefined;
    }
    const refId = rootObj.$ref;
    const referencedRecord = objects.find(
      (objectRecord): objectRecord is { $id?: unknown; $value?: unknown } => (
        objectRecord
        && typeof objectRecord === 'object'
        && (objectRecord as { $id?: unknown }).$id === refId
      )
    );
    const referencedValue = referencedRecord?.$value;
    if (referencedValue && typeof referencedValue === 'object' && !Array.isArray(referencedValue)) {
      const v = referencedValue as Record<string, unknown>;
      if (typeof v.$bc === 'string' && v.$bc.length > 0) {
        return v.$bc;
      }
    }
  }
  return undefined;
}

export function readRootClassFromSerializedData(data: SerializedData): string | undefined {
  return readRootClassFromRecord(data as unknown as Record<string, unknown>);
}

function deepCloneJson<T>(value: T): T {
  if (value === null || value === undefined) {
    return value;
  }
  if (typeof value !== 'object') {
    return value;
  }
  return parseJson(JSON.stringify(value)) as T;
}

export function cloneActorWithNewUUIDs(data: object): object {
  const clone = deepCloneJson(data) as object & { uuid?: string };
  replace_uuids(clone, { deleteKey: false });
  return clone;
}

/**
 * Recursively assigns new `prefabNodeId` values wherever a non-empty id appears.
 * Used by duplication so clones do not inherit template-stable node identity from the source.
 */
export function replace_prefab_node_ids(target: object, options: { deleteKey: boolean } = { deleteKey: false }): void {
  if (Array.isArray(target)) {
    target.forEach(item => replace_prefab_node_ids(item, options));
    return;
  }
  if (typeof target === 'object' && target !== null) {
    for (const [key, child] of Object.entries(target as Record<string, unknown>)) {
      if (key === 'prefabNodeId' && typeof child === 'string' && child.length > 0) {
        if (options.deleteKey) {
          delete (target as Record<string, unknown>)[key];
        } else {
          (target as Record<string, unknown>)[key] = shortUUID();
        }
      } else if (child && typeof child === 'object') {
        replace_prefab_node_ids(child as object, options);
      }
    }
  }
}

/**
 * Deep-clones serialized data for editor duplication: new `uuid` and `prefabNodeId` everywhere they appear.
 */
export function cloneForDuplication(data: object): object {
  const clone = cloneActorWithNewUUIDs(data);
  replace_prefab_node_ids(clone, { deleteKey: false });
  return clone;
}

/**
 * Deep-clones serialized data and rewrites `uuid` string fields using `uuidMap` (from → to).
 */
export function remapSerializedUuids<T extends object>(data: T, uuidMap: Record<string, string>): T {
  const clone = deepCloneJson(data) as T & { uuid?: string };
  if (Object.keys(uuidMap).length === 0) {
    return clone;
  }

  const remapValue = (value: unknown): void => {
    if (!value || typeof value !== 'object') {
      return;
    }
    if (Array.isArray(value)) {
      value.forEach(remapValue);
      return;
    }
    for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
      if (key === 'uuid' && typeof child === 'string') {
        const mapped = uuidMap[child];
        if (mapped) {
          (value as Record<string, unknown>)[key] = mapped;
        }
      } else {
        remapValue(child);
      }
    }
  };

  remapValue(clone);
  return clone;
}

export function collectAssetPathMatches(
  data: unknown,
  targetPath: string,
  basePath: string = ''
): AssetPathReference[] {
  const results: AssetPathReference[] = [];
  if (data == null || typeof data !== 'object') {
    return results;
  }
  if (Array.isArray(data)) {
    data.forEach((item, index) => {
      const p = basePath ? `${basePath}[${index}]` : `[${index}]`;
      if (typeof item === 'string' && pathsEqual(item, targetPath)) {
        results.push({ jsonPath: p, value: item });
      } else {
        results.push(...collectAssetPathMatches(item, targetPath, p));
      }
    });
    return results;
  }
  for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
    const currentPath = basePath ? `${basePath}.${key}` : key;
    if (typeof value === 'string' && pathsEqual(value, targetPath)) {
      results.push({ jsonPath: currentPath, value });
    } else {
      results.push(...collectAssetPathMatches(value, targetPath, currentPath));
    }
  }
  return results;
}

function rewriteStringForAssetPaths(value: string, oldPath: string, newPath: string): string {
  const normalizedOld = normalizeForCompare(oldPath);
  if (normalizeForCompare(value) !== normalizedOld) {
    return value;
  }
  const normalizedNew = normalizeForCompare(newPath);
  if (value.startsWith('@project/')) {
    return normalizedNew;
  }
  return newPath;
}

export function rewriteAssetPathsInClone(data: unknown, oldPath: string, newPath: string): unknown {
  if (data == null) {
    return data;
  }
  if (typeof data === 'string') {
    return rewriteStringForAssetPaths(data, oldPath, newPath);
  }
  if (Array.isArray(data)) {
    return data.map(item => rewriteAssetPathsInClone(item, oldPath, newPath));
  }
  if (typeof data === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
      out[key] = rewriteAssetPathsInClone(value, oldPath, newPath) as never;
    }
    return out;
  }
  return data;
}

/**
 * Recursively finds occurrences of `className` used as the value of a class-name key
 * (`$bc`, `$class`, or legacy `class`) anywhere in `data`. Used to find scenes/prefabs
 * that instantiate a given class before deleting its source file.
 */
export function collectClassNameMatches(
  data: unknown,
  className: string,
  basePath: string = ''
): ClassNameReference[] {
  const results: ClassNameReference[] = [];
  if (data == null || typeof data !== 'object') {
    return results;
  }
  if (Array.isArray(data)) {
    data.forEach((item, index) => {
      const p = basePath ? `${basePath}[${index}]` : `[${index}]`;
      results.push(...collectClassNameMatches(item, className, p));
    });
    return results;
  }
  for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
    const currentPath = basePath ? `${basePath}.${key}` : key;
    if (typeof value === 'string' && CLASS_NAME_KEYS.has(key) && value === className) {
      results.push({ jsonPath: currentPath, value });
      continue;
    }
    if (value && typeof value === 'object') {
      results.push(...collectClassNameMatches(value, className, currentPath));
    }
  }
  return results;
}
